import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, Brain, ChevronDown, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Database, Bot, Send, Link2, Server } from 'lucide-react';
import { fetchPipelineHealth } from '@/api/health';
import type { PipelineHealthResponse, TenantHealthResult, CheckResult } from '@/api/health';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/utils/cn';

const CHECK_ICONS: Record<string, typeof Database> = {
  db_connectivity: Database,
  data_layer: Server,
  waba: Send,
  telegram_bot: Bot,
  telegram_client: Bot,
  max_bot: Send,
  phone_bindings: Link2,
  clinical_context_store: Server,
  messaging_ai_copilot: Brain,
};

const CHECK_LABELS: Record<string, string> = {
  db_connectivity: 'База данных',
  data_layer: 'Data Layer',
  waba: 'WABA',
  telegram_bot: 'Telegram Bot',
  telegram_client: 'Telegram Client',
  max_bot: 'MAX Bot',
  phone_bindings: 'Привязки',
  clinical_context_store: 'Clinical Context',
  messaging_ai_copilot: 'AI Copilot',
};

const STATUS_CONFIG = {
  healthy: { color: 'bg-emerald-500', ringColor: 'ring-emerald-400', text: 'Каналы в норме', textColor: 'text-emerald-700', bgColor: 'bg-shell-success-soft', borderColor: 'border-shell-border' },
  degraded: { color: 'bg-amber-500', ringColor: 'ring-amber-400', text: 'Есть проблемы каналов', textColor: 'text-amber-700', bgColor: 'bg-shell-warning-soft', borderColor: 'border-shell-border' },
  unhealthy: { color: 'bg-red-500', ringColor: 'ring-red-400', text: 'Сервис недоступен', textColor: 'text-red-700', bgColor: 'bg-shell-danger-soft', borderColor: 'border-shell-border' },
} as const;

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);

  if (diffSec < 10) return 'только что';
  if (diffSec < 60) return `${diffSec}с назад`;
  if (diffMin < 60) return `${diffMin}м назад`;
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function CheckRow({ check }: { check: CheckResult }) {
  const Icon = CHECK_ICONS[check.name] || Activity;
  const label = CHECK_LABELS[check.name] || check.name;

  return (
    <div className="flex items-center gap-2 py-1">
      <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
      <span className="text-xs text-slate-600 flex-1 truncate">{label}</span>
      {check.status === 'ok' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
      {check.status === 'warn' && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
      {check.status === 'error' && <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
      <span className="text-[10px] text-slate-400 font-mono w-10 text-right flex-shrink-0">{check.latency_ms}ms</span>
    </div>
  );
}

function TenantSection({ tenant }: { tenant: TenantHealthResult }) {
  const cfg = STATUS_CONFIG[tenant.overall];

  return (
    <div className={cn('rounded-lg border p-2.5', cfg.borderColor, cfg.bgColor)}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className={cn('w-2 h-2 rounded-full flex-shrink-0', cfg.color)} />
        <span className="text-xs font-semibold text-slate-700 truncate">{tenant.tenant_name}</span>
        <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">{tenant.tenant_slug}</span>
      </div>
      <div className="space-y-0.5">
        {tenant.checks.map((check) => (
          <CheckRow key={check.name} check={check} />
        ))}
      </div>
      {tenant.checks.some((c) => c.message && c.status !== 'ok') && (
        <div className="mt-1.5 pt-1.5 border-t border-slate-200/50">
          {tenant.checks
            .filter((c) => c.message && c.status !== 'ok')
            .map((c) => (
              <div key={c.name} className="text-[10px] text-slate-500 truncate">
                {CHECK_LABELS[c.name] || c.name}: {c.message}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export function SystemHealthBadge() {
  const user = useAuthStore((s) => s.user);
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const isSuperAdmin = !user?.tenant_id;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const tenantId = selectedTenantId || undefined;
  const isTenantMode = Boolean(tenantId);

  const { data, isLoading, refetch, isFetching } = useQuery<PipelineHealthResponse>({
    queryKey: ['pipeline-health', tenantId || 'all'],
    queryFn: () => fetchPipelineHealth(false, tenantId),
    enabled: isSuperAdmin,
    staleTime: 25_000,
    refetchInterval: 30_000,
    retry: 1,
  });

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  if (!isSuperAdmin) return null;

  const summaryData = data && 'tenants' in data ? data : null;
  const tenantData = data && 'checks' in data ? data : null;
  const overall = data?.overall ?? 'healthy';
  const cfg = STATUS_CONFIG[overall];
  const tenantsCount = summaryData?.tenants?.length ?? 0;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border transition-all',
          'hover:shadow-sm active:scale-[0.98]',
          isLoading ? 'border-slate-200 bg-slate-50' : `${cfg.borderColor} ${cfg.bgColor}`,
        )}
      >
        <span className="relative flex h-2.5 w-2.5">
          {!isLoading && <span className={cn('relative inline-flex rounded-full h-2.5 w-2.5', cfg.color)} />}
          {isLoading && <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-slate-300" />}
        </span>
        <span className={cn('text-xs font-medium', isLoading ? 'text-slate-400' : cfg.textColor)}>
          {isLoading ? '...' : cfg.text}
        </span>
        <ChevronDown className={cn('w-3 h-3 transition-transform', isLoading ? 'text-slate-400' : cfg.textColor, open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
          <div className="px-3 py-2.5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">
                {isTenantMode ? 'Tenant Health' : 'Pipeline Health'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {data?.checked_at && (
                <span className="text-[10px] text-slate-400">
                  {new Date(data.checked_at).toLocaleString('ru-RU', {
                    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                  {' · '}
                  {formatRelativeTime(data.checked_at)}
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  refetch();
                }}
                className="p-1 rounded hover:bg-slate-200 transition-colors"
                title="Force refresh"
              >
                <RefreshCw className={cn('w-3.5 h-3.5 text-slate-500', isFetching && 'animate-spin')} />
              </button>
            </div>
          </div>

          <div className="p-3 max-h-[400px] overflow-y-auto space-y-2">
            {isLoading && (
              <div className="text-xs text-slate-400 text-center py-4">Loading...</div>
            )}
            {!isLoading && isSuperAdmin && !tenantId && tenantsCount > 0 && summaryData?.tenants?.map((t) => (
              <TenantSection key={t.tenant_id} tenant={t} />
            ))}
            {!isLoading && isSuperAdmin && !tenantId && tenantsCount === 0 && (
              <div className="text-[11px] text-slate-500 text-center py-4">
                Выберите клинику, чтобы видеть состояние каналов конкретного тенанта.
              </div>
            )}
            {!isLoading && tenantData && (
              <TenantSection tenant={tenantData} />
            )}
          </div>

          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/50">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-slate-400">
                {tenantData ? tenantData.tenant_slug : `${tenantsCount} tenant(s)`}
              </span>
              <span className={cn('text-[10px] font-medium', cfg.textColor)}>{overall}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
